import React from "react";
import { useLanguage } from "../contexts/LanguageContext";
import LeftSVG from "../assets/page_svg/vector.svg";
import RightSVG from "../assets/page_svg/Checklist.png";
import Footer from "../components/Footer";
import WhatsAppButton from "../components/WhatsappButton";

const OpeningClosingChecklist: React.FC = () => {
  const { t } = useLanguage();

  const section1 = t("openingClosingChecklist.section1");
  const section2 = t("openingClosingChecklist.section2");
  const section3 = t("openingClosingChecklist.section3");
  const section4 = t("openingClosingChecklist.section4");

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F9DCAC" }}>
      <div className="relative z-10">
        {/* Hero Section */}
        <div className="relative py-20 hero-section-h">
          <div className="absolute inset-0 pointer-events-none z-0">
            <img
              src={LeftSVG}
              alt="left decoration"
              className="absolute top-0 left-0 max-w-[150px] sm:max-w-[220px] lg:max-w-[280px] w-full"
            />
            <img
              src={RightSVG}
              alt="right decoration"
              className="absolute bottom-[-60px] right-0 max-w-[220px] sm:max-w-[220px] md:max-w-[250px] lg:max-w-[350px] w-full"
            />
          </div>
          <div className="relative container mx-auto px-4 z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold mb-6 text-black">
                {t("openingClosingChecklist.title")}
              </h1>
              <p className="text-xl md:text-2xl font-light text-natural-800">
                {t("openingClosingChecklist.subtitle")}
              </p>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            {/* Introduction */}
            <div className="bg-[#FBEACD] rounded-2xl shadow-lg p-8 mb-8">
              <p className="text-lg text-gray-800 mb-6 leading-relaxed">
                {t("openingClosingChecklist.intro")}
              </p>
              <p className="text-lg text-gray-800 leading-relaxed">
                {t("openingClosingChecklist.intro2")}
              </p>
            </div>

            {/* Section 1 */}
            <div className="bg-[#FBEACD] rounded-2xl shadow-lg p-8 mb-8">
              <h2 className="text-3xl font-semibold text-[#492A19] mb-6">
                {section1.title}
              </h2>
              <p className="text-lg text-gray-800 mb-6">{section1.description}</p>
              <ul className="space-y-3 mb-6">
                {section1.purposes.map((purpose: string, idx: number) => (
                  <li key={idx} className="flex items-start">
                    <span className="text-[#492A19] font-semibold mr-3">●</span>
                    <span className="text-gray-800">{purpose}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-orange-50 border-l-4 border-[#492A19] p-4 rounded-2xl">
                <p className="text-gray-800 italic">{section1.note}</p>
              </div>
            </div>

            {/* Section 2 */}
            <div className="bg-[#FBEACD] rounded-2xl shadow-lg p-8 mb-8">
              <h2 className="text-3xl font-semibold text-[#492A19] mb-6">
                {section2.title}
              </h2>
              <p className="text-lg text-gray-800 mb-6">
                {section2.description}
              </p>

              <div className="space-y-8">
                {Object.entries(section2.subsections).map(([key, subsection]: any) => (
                  <div key={key} className="border-l-4 border-[#d57a4a] pl-6">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4">
                      {subsection.title}
                    </h3>
                    <ul className="space-y-3">
                      {subsection.points.map((point: string, idx: number) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-[#492A19] font-semibold mr-3">●</span>
                          <span className="text-gray-800">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 3 */}
            <div className="bg-[#FBEACD] rounded-2xl shadow-lg p-8 mb-8">
              <h2 className="text-3xl font-semibold text-[#492A19] mb-6">
                {section3.title}
              </h2>
              <p className="text-lg text-gray-800 mb-6">
                {section3.description}
              </p>

              <div className="space-y-8 mb-6">
                {Object.entries(section3.subsections).map(([key, subsection]: any) => (
                  <div key={key} className="border-l-4 border-[#d57a4a] pl-6">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4">
                      {subsection.title}
                    </h3>
                    <ul className="space-y-3">
                      {subsection.points.map((point: string, idx: number) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-[#492A19] font-semibold mr-3">●</span>
                          <span className="text-gray-800">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="bg-orange-50 border-l-4 border-[#492A19] p-4 rounded-2xl">
                <p className="text-gray-800 italic">{section3.note}</p>
              </div>
            </div>

            {/* Section 4 */}
            <div className="bg-[#FBEACD] rounded-2xl shadow-lg p-8 mb-8">
              <h2 className="text-3xl font-semibold text-[#492A19] mb-6">
                {section4.title}
              </h2>
              <ul className="space-y-3">
                {section4.points.map((point: string, idx: number) => (
                  <li key={idx} className="flex items-start">
                    <span className="text-[#492A19] font-semibold mr-3">●</span>
                    <span className="text-gray-800">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

        {/* WhatsApp Floating Contact Button */}
        <WhatsAppButton/>
      {/* Footer */}
      <Footer/>
    </div>
  );
};

export default OpeningClosingChecklist;
